import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AtSign,
  Check,
  ChevronDown,
  ChevronUp,
  Clock3,
  ExternalLink,
  FileText,
  Mail,
  Paperclip,
  Plus,
  RotateCcw,
  SendHorizonal,
  Square,
  Trash2,
  X,
} from "lucide-react";
import API from "../../services/api";
import {
  fetchMailArchive,
  deleteMailArchiveItems,
  listDeletedDraftMails,
  listDraftMails,
  moveMailArchiveFolder,
  removeDeletedDraftMail,
  removeDraftMail,
  upsertDeletedDraftMail,
  upsertDraftMail,
} from "../../utils/bulkEmailStorage";
import "./HRBulkEmailsPage.css";

const MAILBOX_SCOPE = "hr-bulk-emails";

const initialForm = {
  from: "<careers@blackroth.in>",
  cc: "",
  bcc: "",
  subject: "",
  description: "",
};

const fromOptions = [
  "<careers@blackroth.in>",
  "<info@blackroth.in>",
  "<hr@blackroth.in>",
];

const mailboxFolders = [
  {
    id: "NEW_EMAIL",
    label: "New Email",
    icon: Plus,
  },
  {
    id: "SENT",
    label: "Sent",
    icon: SendHorizonal,
  },
  {
    id: "DRAFTS",
    label: "Drafts",
    icon: FileText,
  },
  {
    id: "DELETED",
    label: "Deleted",
    icon: Trash2,
  },
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmail = (value) =>
  String(value || "")
    .replace(/[<>]/g, "")
    .trim()
    .toLowerCase();

const parseList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeEmail(item))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(/[,;\n]/)
      .map((item) => normalizeEmail(item))
      .filter(Boolean);
  }

  return [];
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve({
        filename: file.name,
        content: base64,
      });
    };
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

const stringifyObjectId = (value) => {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  if (value && typeof value === "object") {
    return String(value?.$oid || value?.oid || value?.value || "");
  }

  return "";
};

const getArchiveMutationId = (record) =>
  stringifyObjectId(record?.id) || stringifyObjectId(record?._id);

const getArchiveId = (record) => {
  const mutationId = getArchiveMutationId(record);

  if (mutationId) {
    return mutationId;
  }

  const providerId = stringifyObjectId(record?.providerMessageId);

  if (providerId) {
    return providerId;
  }

  return `${record?.createdAt || Date.now()}-${record?.subject || "mail"}`;
};

const normalizeArchiveRecord = (record) => ({
  id: getArchiveId(record),
  archiveId: getArchiveMutationId(record),
  folder: String(record?.folder || "SENT").trim().toUpperCase(),
  mailType: String(record?.mailType || "").trim().toUpperCase(),
  mailTag: String(record?.mailTag || "").trim(),
  from: String(record?.from || "").trim(),
  toRecipients: parseList(record?.toRecipients),
  ccRecipients: parseList(record?.ccRecipients),
  bccRecipients: parseList(record?.bccRecipients),
  subject: String(record?.subject || "").trim(),
  description: String(record?.description || ""),
  attachments: Array.isArray(record?.attachments)
    ? record.attachments.map((attachment, index) => ({
        id: `${getArchiveId(record)}-${index}`,
        filename: String(attachment?.filename || attachment?.name || "attachment"),
        content: String(attachment?.content || ""),
      }))
    : [],
  provider: String(record?.provider || "").trim(),
  providerMessageId: String(record?.providerMessageId || "").trim(),
  relatedId: String(record?.relatedId || "").trim(),
  meta: record?.meta || {},
  createdAt: record?.createdAt || record?.updatedAt || new Date().toISOString(),
  updatedAt: record?.updatedAt || record?.createdAt || new Date().toISOString(),
  preview:
    String(record?.description || "").trim().replace(/\s+/g, " ").slice(0, 120) ||
    "No preview available",
});

const getOfferLetterPdfUrl = (mail) => {
  const offerUrl = String(mail?.meta?.offerUrl || "").trim();

  if (offerUrl) {
    return offerUrl;
  }

  const match = String(mail?.description || "").match(/Offer Letter:\s*(https?:\/\/\S+)/i);
  return match?.[1] || "";
};

const normalizeDraftRecord = (record) => ({
  id: String(record?.id || `draft-${Date.now()}`),
  folder: "DRAFTS",
  mailType: "DRAFT",
  mailTag: "Draft",
  from: String(record?.from || "<careers@blackroth.in>"),
  toRecipients: parseList(record?.toRecipients),
  ccRecipients: parseList(record?.ccRecipients),
  bccRecipients: parseList(record?.bccRecipients),
  subject: String(record?.subject || ""),
  description: String(record?.description || ""),
  attachments: Array.isArray(record?.attachments)
    ? record.attachments.map((attachment, index) => ({
        id: `${String(record?.id || "draft")}-${index}`,
        filename: String(attachment?.filename || attachment?.name || "attachment"),
        content: String(attachment?.content || ""),
      }))
    : [],
  provider: "draft",
  providerMessageId: "",
  relatedId: "",
  meta: record?.meta || {},
  createdAt: record?.createdAt || new Date().toISOString(),
  updatedAt: record?.updatedAt || record?.createdAt || new Date().toISOString(),
  preview:
    String(record?.description || "").trim().replace(/\s+/g, " ").slice(0, 120) ||
    "No preview available",
});

const normalizeDeletedDraftRecord = (record) => ({
  ...normalizeDraftRecord(record),
  folder: "DELETED",
  mailTag: "Deleted Draft",
});

const createDraftSnapshot = ({
  id,
  form,
  recipients,
  ccRecipients,
  bccRecipients,
  attachments,
}) => ({
  id: id || `draft-${Date.now()}`,
  from: form.from,
  toRecipients: [...recipients],
  ccRecipients: [...ccRecipients],
  bccRecipients: [...bccRecipients],
  subject: form.subject,
  description: form.description,
  attachments: attachments.map((attachment) => ({
    filename: attachment.filename,
    content: attachment.content,
  })),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const HRBulkEmailsPage = () => {
  const [form, setForm] = useState(initialForm);
  const [recipientInput, setRecipientInput] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [ccRecipients, setCcRecipients] = useState([]);
  const [bccInput, setBccInput] = useState("");
  const [bccRecipients, setBccRecipients] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [activeFolder, setActiveFolder] = useState("NEW_EMAIL");
  const [archiveItems, setArchiveItems] = useState([]);
  const [draftItems, setDraftItems] = useState(() =>
    listDraftMails(MAILBOX_SCOPE).map(normalizeDraftRecord),
  );
  const [deletedDraftItems, setDeletedDraftItems] = useState(() =>
    listDeletedDraftMails(MAILBOX_SCOPE).map(normalizeDeletedDraftRecord),
  );
  const [selectedMailId, setSelectedMailId] = useState("");
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [openedMail, setOpenedMail] = useState(null);
  const [hoveredMailId, setHoveredMailId] = useState("");
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const [editingDraftId, setEditingDraftId] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    ids: [],
    permanent: false,
    label: "",
  });
  const previousActiveFolderRef = useRef(activeFolder);

  const refreshDrafts = useCallback(() => {
    const nextDrafts = listDraftMails(MAILBOX_SCOPE).map(normalizeDraftRecord);
    setDraftItems(nextDrafts);
    return nextDrafts;
  }, []);

  const refreshDeletedDrafts = useCallback(() => {
    const nextDeletedDrafts = listDeletedDraftMails(MAILBOX_SCOPE).map(
      normalizeDeletedDraftRecord,
    );
    setDeletedDraftItems(nextDeletedDrafts);
    return nextDeletedDrafts;
  }, []);

  const refreshArchive = async () => {
    try {
      setArchiveLoading(true);
      setArchiveError("");
      const records = await fetchMailArchive({
        folder: "ALL",
        limit: 200,
      });
      setArchiveItems(records.map(normalizeArchiveRecord));
    } catch (error) {
      setArchiveError(
        error.response?.data?.message ||
          error.message ||
          "Unable to load mail archive.",
      );
    } finally {
      setArchiveLoading(false);
    }
  };

  useEffect(() => {
    refreshArchive();
  }, []);

  useEffect(() => {
    if (activeFolder === "DRAFTS") {
      refreshDrafts();
    }
    if (activeFolder === "DELETED") {
      refreshDeletedDrafts();
    }
    setSelectedMailIds([]);
    setOpenedMail(null);
  }, [activeFolder, refreshDeletedDrafts, refreshDrafts]);

  const recipientCount = useMemo(() => recipients.length, [recipients]);

  const visibleItems = useMemo(() => {
    return (
      activeFolder === "DRAFTS"
        ? draftItems
        : activeFolder === "NEW_EMAIL"
          ? []
          : activeFolder === "DELETED"
            ? [
                ...archiveItems.filter((item) => item.folder === "DELETED"),
                ...deletedDraftItems,
              ]
            : archiveItems.filter((item) => item.folder === activeFolder)
    );
  }, [activeFolder, archiveItems, deletedDraftItems, draftItems]);

  useEffect(() => {
    if (activeFolder === "NEW_EMAIL") {
      setSelectedMailId("");
      return;
    }

    if (!visibleItems.length) {
      setSelectedMailId("");
      return;
    }

    setSelectedMailId((current) =>
      visibleItems.some((item) => item.id === current)
        ? current
        : visibleItems[0].id,
    );
  }, [activeFolder, visibleItems]);

  const folderCounts = useMemo(() => {
    const counts = {
      SENT: archiveItems.filter((item) => item.folder === "SENT").length,
      DRAFTS: draftItems.length,
      DELETED:
        archiveItems.filter((item) => item.folder === "DELETED").length +
        deletedDraftItems.length,
    };

    return counts;
  }, [archiveItems, deletedDraftItems.length, draftItems.length]);

  const hasDraftContent = useMemo(
    () =>
      recipients.length > 0 ||
      ccRecipients.length > 0 ||
      bccRecipients.length > 0 ||
      Boolean(form.subject.trim()) ||
      Boolean(form.description.trim()) ||
      attachments.length > 0,
    [
      attachments.length,
      bccRecipients.length,
      ccRecipients.length,
      form.description,
      form.subject,
      recipients.length,
    ],
  );

  const saveCurrentDraft = useCallback(
    ({ showFeedback = false, openDrafts = false } = {}) => {
      if (!hasDraftContent) {
        return null;
      }

      const draft = normalizeDraftRecord(
        createDraftSnapshot({
          id: editingDraftId,
          form,
          recipients,
          ccRecipients,
          bccRecipients,
          attachments,
        }),
      );

      upsertDraftMail(
        {
          id: draft.id,
          from: draft.from,
          toRecipients: draft.toRecipients,
          ccRecipients: draft.ccRecipients,
          bccRecipients: draft.bccRecipients,
          subject: draft.subject,
          description: draft.description,
          attachments: draft.attachments,
          createdAt: draft.createdAt,
          updatedAt: draft.updatedAt,
        },
        MAILBOX_SCOPE,
      );

      const nextDrafts = refreshDrafts();
      setEditingDraftId(draft.id);

      if (openDrafts) {
        setActiveFolder("DRAFTS");
        setSelectedMailId(draft.id);
      }

      if (showFeedback) {
        setStatus({
          type: "success",
          message: `Draft saved. ${nextDrafts.length} draft${nextDrafts.length === 1 ? "" : "s"} in the folder.`,
        });
      }

      return draft;
    },
    [
      attachments,
      bccRecipients,
      ccRecipients,
      editingDraftId,
      form,
      hasDraftContent,
      recipients,
      refreshDrafts,
    ],
  );

  useEffect(() => {
    const previousActiveFolder = previousActiveFolderRef.current;

    if (previousActiveFolder === "NEW_EMAIL" && activeFolder !== "NEW_EMAIL") {
      saveCurrentDraft();
    }

    previousActiveFolderRef.current = activeFolder;
  }, [activeFolder, saveCurrentDraft]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activeFolder === "NEW_EMAIL") {
        saveCurrentDraft();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [activeFolder, saveCurrentDraft]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
    setStatus({ type: "", message: "" });
  };

  const addRecipient = (value) => {
    const normalized = normalizeEmail(value);

    if (!normalized) {
      return;
    }

    if (!emailPattern.test(normalized)) {
      setStatus({ type: "error", message: `Invalid email: ${value}` });
      return;
    }

    setRecipients((current) =>
      current.includes(normalized) ? current : [...current, normalized],
    );
    setRecipientInput("");
    setStatus({ type: "", message: "" });
  };

  const addCcRecipient = (value) => {
    const normalized = normalizeEmail(value);

    if (!normalized) {
      return;
    }

    if (!emailPattern.test(normalized)) {
      setStatus({ type: "error", message: `Invalid CC email: ${value}` });
      return;
    }

    setCcRecipients((current) =>
      current.includes(normalized) ? current : [...current, normalized],
    );
    setCcInput("");
    setStatus({ type: "", message: "" });
  };

  const addBccRecipient = (value) => {
    const normalized = normalizeEmail(value);

    if (!normalized) {
      return;
    }

    if (!emailPattern.test(normalized)) {
      setStatus({ type: "error", message: `Invalid BCC email: ${value}` });
      return;
    }

    setBccRecipients((current) =>
      current.includes(normalized) ? current : [...current, normalized],
    );
    setBccInput("");
    setStatus({ type: "", message: "" });
  };

  const handleRecipientKeyDown = (event) => {
    if (["Enter", "Tab", ","].includes(event.key)) {
      event.preventDefault();
      addRecipient(recipientInput);
    }

    if (event.key === "Backspace" && !recipientInput && recipients.length) {
      setRecipients((current) => current.slice(0, -1));
    }
  };

  const handleRecipientPaste = (event) => {
    const pastedText = event.clipboardData.getData("text");
    const values = pastedText
      .split(/[,;\n]/)
      .map((item) => normalizeEmail(item))
      .filter(Boolean);

    if (values.length > 1) {
      event.preventDefault();
      setRecipients((current) => {
        const next = [...current];
        values.forEach((email) => {
          if (emailPattern.test(email) && !next.includes(email)) {
            next.push(email);
          }
        });
        return next;
      });
      setStatus({ type: "", message: "" });
    }
  };

  const removeRecipient = (email) => {
    setRecipients((current) => current.filter((item) => item !== email));
  };

  const removeCcRecipient = (email) => {
    setCcRecipients((current) => current.filter((item) => item !== email));
  };

  const removeBccRecipient = (email) => {
    setBccRecipients((current) => current.filter((item) => item !== email));
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    const uploaded = await Promise.all(files.map(readFileAsBase64));
    setAttachments((current) => [
      ...current,
      ...uploaded.map((item, index) => ({
        id: `${item.filename}-${Date.now()}-${index}`,
        filename: item.filename,
        content: item.content,
      })),
    ]);

    event.target.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((current) => current.filter((item) => item.id !== id));
  };

  const resetComposer = () => {
    setForm({
      ...initialForm,
      from: form.from,
    });
    setRecipients([]);
    setRecipientInput("");
    setCcRecipients([]);
    setCcInput("");
    setBccRecipients([]);
    setBccInput("");
    setAttachments([]);
    setShowMore(false);
    setEditingDraftId("");
    setStatus({ type: "", message: "" });
  };

  const loadDraftIntoComposer = (draft) => {
    setForm({
      from: draft.from || initialForm.from,
      cc: "",
      bcc: "",
      subject: draft.subject || "",
      description: draft.description || "",
    });
    setRecipients([...(draft.toRecipients || [])]);
    setRecipientInput("");
    setCcRecipients([...(draft.ccRecipients || [])]);
    setCcInput("");
    setBccRecipients([...(draft.bccRecipients || [])]);
    setBccInput("");
    setAttachments(
      (draft.attachments || []).map((attachment, index) => ({
        id: `${draft.id}-${index}`,
        filename: attachment.filename,
        content: attachment.content || "",
      })),
    );
    setShowMore(Boolean((draft.ccRecipients || []).length || (draft.bccRecipients || []).length));
    setEditingDraftId(draft.id);
    setActiveFolder("NEW_EMAIL");
    setStatus({
      type: "success",
      message: "Draft loaded into the composer.",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });

    if (!recipients.length) {
      setStatus({
        type: "error",
        message: "Please enter at least one recipient in To.",
      });
      return;
    }

    if (!form.subject.trim()) {
      setStatus({ type: "error", message: "Please add a subject." });
      return;
    }

    if (!form.description.trim()) {
      setStatus({ type: "error", message: "Please add a message body." });
      return;
    }

    try {
      setIsSending(true);

      await API.post("/admin/bulk-email/send", {
        ...form,
        to: recipients.join(", "),
        cc: ccRecipients.join(", "),
        bcc: bccRecipients.join(", "),
        attachments,
      });

      setStatus({
        type: "success",
        message: `Mail sent to ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}.`,
      });
      resetComposer();
      setActiveFolder("SENT");
      await refreshArchive();
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to send the email.",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleSendDraftMail = async (draft) => {
    const draftRecipients = draft?.toRecipients || [];

    if (!draftRecipients.length) {
      setOpenedMail(null);
      setStatus({
        type: "error",
        message: "Please edit this draft and add at least one recipient before sending.",
      });
      return;
    }

    if (!String(draft?.subject || "").trim()) {
      setOpenedMail(null);
      setStatus({
        type: "error",
        message: "Please edit this draft and add a subject before sending.",
      });
      return;
    }

    if (!String(draft?.description || "").trim()) {
      setOpenedMail(null);
      setStatus({
        type: "error",
        message: "Please edit this draft and add a message body before sending.",
      });
      return;
    }

    try {
      setIsSending(true);

      await API.post("/admin/bulk-email/send", {
        from: draft.from,
        cc: draft.ccRecipients.join(", "),
        bcc: draft.bccRecipients.join(", "),
        subject: draft.subject,
        description: draft.description,
        to: draftRecipients.join(", "),
        attachments: draft.attachments,
      });

      removeDraftMail(draft.id, MAILBOX_SCOPE);
      refreshDrafts();
      setSelectedMailIds([]);
      setSelectedMailId("");
      setOpenedMail(null);
      setStatus({
        type: "success",
        message: `Mail sent to ${draftRecipients.length} recipient${draftRecipients.length === 1 ? "" : "s"}.`,
      });
      setActiveFolder("SENT");
      await refreshArchive();
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to send the draft email.",
      });
    } finally {
      setIsSending(false);
    }
  };

  const openDeleteDialog = ({ ids, permanent, label }) => {
    const normalizedIds = Array.isArray(ids) ? ids.filter(Boolean) : [];

    if (!normalizedIds.length) {
      return;
    }

    setDeleteDialog({
      open: true,
      ids: normalizedIds,
      permanent: Boolean(permanent),
      label: label || "this mail",
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      ids: [],
      permanent: false,
      label: "",
    });
  };

  const getArchiveSelection = (ids) => {
    const selectedArchiveItems = archiveItems.filter((item) =>
      ids.includes(item.id),
    );
    const missingIdCount = selectedArchiveItems.filter(
      (item) => !item.archiveId,
    ).length;

    return {
      selectedArchiveItems,
      requestIds: selectedArchiveItems
        .map((item) => item.archiveId)
        .filter(Boolean),
      missingIdCount,
    };
  };

  const executeDelete = async ({ ids, permanent }) => {
    const normalizedIds = Array.isArray(ids) ? ids.filter(Boolean) : [];

    if (!normalizedIds.length) {
      return;
    }

    try {
      if (activeFolder === "DRAFTS") {
        const draftsToDelete = draftItems.filter((item) =>
          normalizedIds.includes(item.id),
        );

        draftsToDelete.forEach((draft) => {
          upsertDeletedDraftMail(
            {
              ...draft,
              folder: "DELETED",
              mailTag: "Deleted Draft",
              updatedAt: new Date().toISOString(),
            },
            MAILBOX_SCOPE,
          );
          removeDraftMail(draft.id, MAILBOX_SCOPE);
        });

        refreshDrafts();
        refreshDeletedDrafts();
      } else if (permanent || activeFolder === "DELETED") {
        const deletedDraftIds = deletedDraftItems.map((item) => item.id);
        const localDraftIds = normalizedIds.filter((id) =>
          deletedDraftIds.includes(id),
        );
        const archiveIds = normalizedIds.filter(
          (id) => !deletedDraftIds.includes(id),
        );
        const archiveSelection = getArchiveSelection(archiveIds);

        localDraftIds.forEach((id) => removeDeletedDraftMail(id, MAILBOX_SCOPE));

        if (archiveSelection.missingIdCount) {
          throw new Error("Unable to delete one or more selected archive mails.");
        }

        if (archiveSelection.requestIds.length) {
          await deleteMailArchiveItems(archiveSelection.requestIds);
          await refreshArchive();
        }

        refreshDeletedDrafts();
      } else {
        const archiveSelection = getArchiveSelection(normalizedIds);

        if (archiveSelection.missingIdCount) {
          throw new Error("Unable to move one or more selected archive mails.");
        }

        if (archiveSelection.requestIds.length) {
          await moveMailArchiveFolder(archiveSelection.requestIds, "DELETED");
          await refreshArchive();
        }
      }

      setSelectedMailIds([]);
      setSelectedMailId("");
      setOpenedMail(null);
      setStatus({
        type: "success",
        message:
          permanent || activeFolder === "DELETED"
            ? "Selected mails deleted."
            : activeFolder === "DRAFTS"
              ? "Selected drafts moved to Deleted."
              : "Selected mails moved to Deleted.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to delete selected mails.",
      });
    }
  };

  const handleDeleteSelected = () => {
    openDeleteDialog({
      ids: selectedMailIds,
      permanent: activeFolder === "DELETED",
      label:
        selectedMailIds.length === 1
          ? "this selected mail"
          : `${selectedMailIds.length} selected mails`,
    });
  };

  const handleRestoreSelected = async () => {
    const normalizedIds = selectedMailIds.filter(Boolean);

    if (!normalizedIds.length) {
      return;
    }

    try {
      const deletedDraftIds = deletedDraftItems.map((item) => item.id);
      const localDraftIds = normalizedIds.filter((id) =>
        deletedDraftIds.includes(id),
      );
      const archiveIds = normalizedIds.filter(
        (id) => !deletedDraftIds.includes(id),
      );
      const archiveSelection = getArchiveSelection(archiveIds);

      localDraftIds.forEach((id) => {
        const deletedDraft = deletedDraftItems.find((item) => item.id === id);

        if (deletedDraft) {
          upsertDraftMail(
            {
              ...deletedDraft,
              folder: "DRAFTS",
              mailTag: "Draft",
              updatedAt: new Date().toISOString(),
            },
            MAILBOX_SCOPE,
          );
          removeDeletedDraftMail(id, MAILBOX_SCOPE);
        }
      });

      if (archiveSelection.missingIdCount) {
        throw new Error("Unable to restore one or more selected archive mails.");
      }

      if (archiveSelection.requestIds.length) {
        await moveMailArchiveFolder(archiveSelection.requestIds, "SENT");
        await refreshArchive();
      }

      refreshDrafts();
      refreshDeletedDrafts();
      setSelectedMailIds([]);
      setSelectedMailId("");
      setOpenedMail(null);
      setStatus({
        type: "success",
        message:
          normalizedIds.length === 1
            ? "Selected mail restored."
            : "Selected mails restored.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to restore selected mails.",
      });
    }
  };

  const activeFolderConfig =
    mailboxFolders.find((folder) => folder.id === activeFolder) || mailboxFolders[0];
  const ActiveFolderIcon = activeFolderConfig.icon;

  return (
    <section className="bulk-emails-page">
      <article className="hr-page-card bulk-emails-card">
        <div className="bulk-mailbox-shell">
          <aside className="bulk-mailbox-sidebar">
            <button
              type="button"
              className="bulk-sidebar-compose-btn"
              onClick={() => {
                saveCurrentDraft();
                resetComposer();
                setActiveFolder("NEW_EMAIL");
              }}
            >
              <Plus size={18} />
              New Email
            </button>

            <nav className="bulk-sidebar-nav" aria-label="Mail folders">
              {mailboxFolders
                .filter((folder) => folder.id !== "NEW_EMAIL")
                .map((folder) => {
                  const Icon = folder.icon;
                  const isActive = activeFolder === folder.id;
                  return (
                    <button
                      key={folder.id}
                      type="button"
                      className={`bulk-sidebar-folder ${isActive ? "is-active" : ""}`}
                      onClick={() => {
                        setActiveFolder(folder.id);
                      }}
                    >
                      <span className="bulk-sidebar-folder-icon">
                        <Icon size={18} />
                      </span>
                      <span className="bulk-sidebar-folder-label">{folder.label}</span>
                      <span className="bulk-sidebar-folder-count">
                        {folderCounts[folder.id] || 0}
                      </span>
                    </button>
                  );
                })}
            </nav>
          </aside>

          <div className="bulk-mailbox-main">
            {activeFolder === "NEW_EMAIL" ? (
              <div className="bulk-mailbox-composer">
                <div className="hr-page-title">
                  <Mail size={20} />
                  <h2>Bulk Emails</h2>
                </div>

                <p className="bulk-emails-intro">
                  Send interview-related emails to multiple recipients using the
                  addresses in To. Separate emails with commas.
                </p>

                <form className="bulk-email-form" onSubmit={handleSubmit}>
                  <label className="bulk-field">
                    <span>
                      <Mail size={14} />
                      From
                    </span>
                    <select name="from" value={form.from} onChange={handleChange}>
                      {fromOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="bulk-field">
                    <span>
                      <AtSign size={14} />
                      To
                    </span>
                    <div className="bulk-recipient-box">
                      <div className="bulk-recipient-chips">
                        {recipients.map((email) => (
                          <span className="bulk-recipient-chip" key={email}>
                            {email}
                            <button
                              type="button"
                              className="bulk-chip-remove"
                              onClick={() => removeRecipient(email)}
                              aria-label={`Remove ${email}`}
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          value={recipientInput}
                          onChange={(event) => {
                            setRecipientInput(event.target.value);
                            setStatus({ type: "", message: "" });
                          }}
                          onKeyDown={handleRecipientKeyDown}
                          onBlur={() => {
                            if (recipientInput.trim()) {
                              addRecipient(recipientInput);
                            }
                          }}
                          onPaste={handleRecipientPaste}
                          placeholder={
                            recipients.length
                              ? "Add another email..."
                              : "Type email and press Enter"
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="bulk-more-btn"
                    onClick={() => setShowMore((current) => !current)}
                  >
                    {showMore ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {showMore ? "Hide CC / BCC" : "Show CC / BCC"}
                  </button>

                  {showMore && (
                    <div className="bulk-extra-fields">
                      <div className="bulk-field">
                        <span>Cc</span>
                        <div className="bulk-recipient-box">
                          <div className="bulk-recipient-chips">
                            {ccRecipients.map((email) => (
                              <span className="bulk-recipient-chip" key={email}>
                                {email}
                                <button
                                  type="button"
                                  className="bulk-chip-remove"
                                  onClick={() => removeCcRecipient(email)}
                                  aria-label={`Remove ${email}`}
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                            <input
                              type="text"
                              value={ccInput}
                              onChange={(event) => {
                                setCcInput(event.target.value);
                                setStatus({ type: "", message: "" });
                              }}
                              onKeyDown={(event) => {
                                if (["Enter", "Tab", ","].includes(event.key)) {
                                  event.preventDefault();
                                  addCcRecipient(ccInput);
                                }
                                if (
                                  event.key === "Backspace" &&
                                  !ccInput &&
                                  ccRecipients.length
                                ) {
                                  setCcRecipients((current) => current.slice(0, -1));
                                }
                              }}
                              onBlur={() => {
                                if (ccInput.trim()) {
                                  addCcRecipient(ccInput);
                                }
                              }}
                              onPaste={(event) => {
                                const pastedText = event.clipboardData.getData("text");
                                const values = pastedText
                                  .split(/[,;\n]/)
                                  .map((item) => normalizeEmail(item))
                                  .filter(Boolean);

                                if (values.length > 1) {
                                  event.preventDefault();
                                  setCcRecipients((current) => {
                                    const next = [...current];
                                    values.forEach((email) => {
                                      if (
                                        emailPattern.test(email) &&
                                        !next.includes(email)
                                      ) {
                                        next.push(email);
                                      }
                                    });
                                    return next;
                                  });
                                  setStatus({ type: "", message: "" });
                                }
                              }}
                              placeholder={
                                ccRecipients.length
                                  ? "Add another email..."
                                  : "Type CC email and press Enter"
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bulk-field">
                        <span>Bcc</span>
                        <div className="bulk-recipient-box">
                          <div className="bulk-recipient-chips">
                            {bccRecipients.map((email) => (
                              <span className="bulk-recipient-chip" key={email}>
                                {email}
                                <button
                                  type="button"
                                  className="bulk-chip-remove"
                                  onClick={() => removeBccRecipient(email)}
                                  aria-label={`Remove ${email}`}
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                            <input
                              type="text"
                              value={bccInput}
                              onChange={(event) => {
                                setBccInput(event.target.value);
                                setStatus({ type: "", message: "" });
                              }}
                              onKeyDown={(event) => {
                                if (["Enter", "Tab", ","].includes(event.key)) {
                                  event.preventDefault();
                                  addBccRecipient(bccInput);
                                }
                                if (
                                  event.key === "Backspace" &&
                                  !bccInput &&
                                  bccRecipients.length
                                ) {
                                  setBccRecipients((current) => current.slice(0, -1));
                                }
                              }}
                              onBlur={() => {
                                if (bccInput.trim()) {
                                  addBccRecipient(bccInput);
                                }
                              }}
                              onPaste={(event) => {
                                const pastedText = event.clipboardData.getData("text");
                                const values = pastedText
                                  .split(/[,;\n]/)
                                  .map((item) => normalizeEmail(item))
                                  .filter(Boolean);

                                if (values.length > 1) {
                                  event.preventDefault();
                                  setBccRecipients((current) => {
                                    const next = [...current];
                                    values.forEach((email) => {
                                      if (
                                        emailPattern.test(email) &&
                                        !next.includes(email)
                                      ) {
                                        next.push(email);
                                      }
                                    });
                                    return next;
                                  });
                                  setStatus({ type: "", message: "" });
                                }
                              }}
                              placeholder={
                                bccRecipients.length
                                  ? "Add another email..."
                                  : "Type BCC email and press Enter"
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <label className="bulk-field">
                    <span>
                      <FileText size={14} />
                      Subject
                    </span>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Add a subject"
                    />
                  </label>

                  <div className="bulk-description-wrap">
                    <div className="bulk-description-header">
                      <span>Message</span>
                      <div className="bulk-toolbar">
                        <label
                          className="bulk-icon-btn bulk-file-btn"
                          aria-label="Attach file"
                        >
                          <Paperclip size={16} />
                          <input
                            type="file"
                            multiple
                            onChange={handleAttachmentChange}
                          />
                        </label>
                      </div>
                    </div>

                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      placeholder="Write your email here..."
                      rows={9}
                    />

                    {attachments.length > 0 && (
                      <div className="bulk-attachment-list">
                        {attachments.map((item) => (
                          <div className="bulk-attachment-item" key={item.id}>
                            <span>{item.filename}</span>
                            <button
                              type="button"
                              className="bulk-remove-btn"
                              onClick={() => removeAttachment(item.id)}
                              aria-label={`Remove ${item.filename}`}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bulk-footer">
                    <p className="bulk-helper-text">
                      Recipient count: <strong>{recipientCount}</strong>
                    </p>

                    <div className="bulk-footer-actions">
                      <button
                        type="submit"
                        className="bulk-primary-btn"
                        disabled={isSending}
                      >
                        <SendHorizonal size={16} />
                        {isSending ? "Sending..." : "Send"}
                      </button>
                    </div>
                  </div>

                  {status.message && (
                    <div
                      className={`bulk-status ${status.type === "error" ? "error" : "success"}`}
                    >
                      {status.message}
                    </div>
                  )}
                </form>
              </div>
            ) : (
              <div className="bulk-mailbox-reader">
                <div className="bulk-mailbox-header">
                  <div>
                    <div className="bulk-mailbox-title-row">
                      <div className="hr-page-title">
                        <ActiveFolderIcon size={20} />
                        <h2>{activeFolderConfig.label}</h2>
                      </div>
                    </div>

                    <p className="bulk-emails-intro">
                      {activeFolder === "SENT"
                        ? "Review every sent message with the full To, Cc, Bcc and body details."
                        : activeFolder === "DRAFTS"
                          ? "Drafts are saved automatically while you work. Open one to edit or send."
                          : activeFolder === "DELETED"
                            ? "These mails were moved here from Sent."
                            : "Browse the full archive from the mailbox perspective."}
                    </p>
                  </div>

                  {selectedMailIds.length > 0 && (
                    <div className="bulk-selected-actions">
                      <button
                        type="button"
                        className="bulk-delete-selected-btn"
                        onClick={handleDeleteSelected}
                      >
                        <Trash2 size={16} />
                        Delete {selectedMailIds.length}
                      </button>

                      {activeFolder === "DELETED" && (
                        <button
                          type="button"
                          className="bulk-restore-selected-btn"
                          onClick={handleRestoreSelected}
                        >
                          <RotateCcw size={16} />
                          Restore {selectedMailIds.length}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {archiveError && (
                  <div className="bulk-status error">{archiveError}</div>
                )}

                {archiveLoading ? (
                  <div className="bulk-mail-empty-state">Loading mails...</div>
                ) : visibleItems.length === 0 ? (
                  <div className="bulk-mail-empty-state">
                    No mails found in {activeFolderConfig.label.toLowerCase()}.
                  </div>
                ) : (
                  <div className="bulk-mail-reader-grid">
                    <div className="bulk-mail-list">
                      {visibleItems.map((item) => {
                        const checked = selectedMailIds.includes(item.id);
                        const isActive = selectedMailId === item.id;
                        const showCheckbox =
                          checked || hoveredMailId === item.id || activeFolder === "DRAFTS";

                        return (
                          <div
                            key={item.id}
                            className={`bulk-mail-row ${isActive ? "is-active" : ""} ${checked ? "is-selected" : ""}`}
                            onMouseEnter={() => setHoveredMailId(item.id)}
                            onMouseLeave={() => setHoveredMailId("")}
                            onClick={() => {
                              setSelectedMailId(item.id);
                              setOpenedMail(item);
                            }}
                          >
                            <span className="bulk-mail-row-check">
                              <button
                                type="button"
                                className={`bulk-row-checkbox ${showCheckbox ? "is-visible" : ""}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (activeFolder === "DRAFTS") {
                                    setSelectedMailIds((current) =>
                                      current.includes(item.id)
                                        ? current.filter((id) => id !== item.id)
                                        : [...current, item.id],
                                    );
                                    setSelectedMailId(item.id);
                                    return;
                                  }

                                  setSelectedMailIds((current) =>
                                    current.includes(item.id)
                                      ? current.filter((id) => id !== item.id)
                                      : [...current, item.id],
                                  );
                                  setSelectedMailId(item.id);
                                }}
                                aria-label={`Select ${item.subject || "mail"}`}
                              >
                                {checked ? <Check size={14} /> : <Square size={14} />}
                              </button>
                            </span>

                            <span className="bulk-mail-row-main">
                              <span className="bulk-mail-row-topline">
                                <strong>{item.subject || "(No subject)"}</strong>
                                <span className="bulk-mail-row-time">
                                  <Clock3 size={12} />
                                  {formatDateTime(item.createdAt)}
                                </span>
                              </span>

                              <span className="bulk-mail-row-meta">
                                <span>{item.from || "Unknown sender"}</span>
                                <span className="bulk-mail-row-dot">•</span>
                                <span>
                                  To:{" "}
                                  {item.toRecipients.length
                                    ? item.toRecipients.join(", ")
                                    : "-"}
                                </span>
                              </span>

                              <span className="bulk-mail-row-preview">{item.preview}</span>
                            </span>

                           
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {deleteDialog.open && (
          <div className="bulk-delete-modal-overlay" role="presentation">
            <div
              className="bulk-delete-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bulk-delete-modal-title"
              aria-describedby="bulk-delete-modal-description"
            >
              <div className="bulk-delete-modal-icon">
                <Trash2 size={18} />
              </div>
              <h3 id="bulk-delete-modal-title">
                {deleteDialog.permanent ? "Delete mail?" : "Move to Deleted?"}
              </h3>
              <p id="bulk-delete-modal-description">
                {deleteDialog.permanent
                  ? `Are you sure you want to delete ${deleteDialog.label || "this mail"}?`
                  : `Are you sure you want to move ${deleteDialog.label || "this mail"} to Deleted?`}
              </p>
              <div className="bulk-delete-modal-actions">
                <button
                  type="button"
                  className="bulk-secondary-btn"
                  onClick={closeDeleteDialog}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bulk-primary-btn"
                  onClick={async () => {
                    const payload = { ...deleteDialog };
                    closeDeleteDialog();
                    await executeDelete(payload);
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {openedMail && (
          <div className="bulk-mail-modal-overlay" role="presentation">
            <div
              className="bulk-mail-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bulk-mail-modal-title"
            >
              <div className="bulk-mail-detail-header">
                <div>
                  <span className="bulk-mail-detail-tag">
                    {openedMail.mailTag || openedMail.folder}
                  </span>
                  <h3 id="bulk-mail-modal-title">
                    {openedMail.subject || "(No subject)"}
                  </h3>
                </div>

                <div className="bulk-mail-modal-actions">
                  {openedMail.folder === "DRAFTS" && (
                    <>
                      <button
                        type="button"
                        className="bulk-secondary-btn"
                        onClick={() => {
                          loadDraftIntoComposer(openedMail);
                          setOpenedMail(null);
                        }}
                      >
                        <RotateCcw size={16} />
                        Edit
                      </button>

                      <button
                        type="button"
                        className="bulk-primary-btn"
                        disabled={isSending}
                        onClick={() => handleSendDraftMail(openedMail)}
                      >
                        <SendHorizonal size={16} />
                        {isSending ? "Sending..." : "Send"}
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    className="bulk-mail-modal-close"
                    aria-label="Close mail"
                    onClick={() => setOpenedMail(null)}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="bulk-mail-detail-body">
                <div className="bulk-mail-detail-row">
                  <span>From</span>
                  <strong>{openedMail.from || "-"}</strong>
                </div>

                <div className="bulk-mail-detail-row">
                  <span>To</span>
                  <strong>
                    {openedMail.toRecipients.length
                      ? openedMail.toRecipients.join(", ")
                      : "-"}
                  </strong>
                </div>

                <div className="bulk-mail-detail-row">
                  <span>Cc</span>
                  <strong>
                    {openedMail.ccRecipients.length
                      ? openedMail.ccRecipients.join(", ")
                      : "-"}
                  </strong>
                </div>

                <div className="bulk-mail-detail-row">
                  <span>Bcc</span>
                  <strong>
                    {openedMail.bccRecipients.length
                      ? openedMail.bccRecipients.join(", ")
                      : "-"}
                  </strong>
                </div>

                <div className="bulk-mail-detail-row">
                  <span>Folder</span>
                  <strong>{openedMail.folder}</strong>
                </div>

                <div className="bulk-mail-detail-row">
                  <span>Sent on</span>
                  <strong>{formatDateTime(openedMail.createdAt)}</strong>
                </div>

                {openedMail.attachments.length > 0 && (
                  <div className="bulk-mail-detail-attachments">
                    <span>Attachments</span>
                    <div className="bulk-attachment-list">
                      {openedMail.attachments.map((attachment) => (
                        <div className="bulk-attachment-item" key={attachment.id}>
                          <span>{attachment.filename}</span>
                          {getOfferLetterPdfUrl(openedMail) && (
                            <a
                              className="bulk-attachment-view-btn"
                              href={getOfferLetterPdfUrl(openedMail)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <ExternalLink size={14} />
                              View
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bulk-mail-detail-message">
                  <span>Message</span>
                  <pre>{openedMail.description || "No message content."}</pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </article>
    </section>
  );
};

export default HRBulkEmailsPage;
